import { BoxButton, Description, Divider, Svg, Table, TableCell } from '@repo/ui';
import { useRouter } from 'next/router';

import { ResultTableRow } from '@/components/tax-refund/apply/result/ResultTableRow';
import ApplyResultInfoView from '@/components/tax-refund/common/view/ApplyResultInfoView';
import { getCorporationDefaultInfo, getIndividualDefaultInfo } from '@/lib/constants/info-contents';
import { COMMON_PATHS } from '@/lib/constants/paths';
import { ApplyResultData } from '@/lib/types/request';
import { formatAccountBankNo } from '@/lib/utils/stringFormat';

export const ApplyResultUnknownContent = ({ data }: { data: ApplyResultData }) => {
  const router = useRouter();
  const isCorps = data.businessType === 'corps';
  const displayName = isCorps ? (data.fullName ?? data.name) : data.name;
  const amountLabel = isCorps ? '법인세 예상 환급액' : '종합소득세 예상 환급액';
  const paymentCard = data.paymentCard;
  const hasRegisteredCard = Boolean(paymentCard?.hasRegisteredCard && paymentCard.cardNumber);
  const cardName = paymentCard?.cardName ?? '';
  const cardLogo = paymentCard?.cardLogo ?? '';
  const cardNumber = paymentCard?.cardNumber ?? '';

  const handleCardClick = () => {
    router.push(COMMON_PATHS.환급결제카드입력);
  };

  const infoContents = isCorps ? getCorporationDefaultInfo() : getIndividualDefaultInfo(hasRegisteredCard);

  return (
    <div>
      <div className={'px-4.5 py-normal-small'}>
        <Description
          title={isCorps ? `’${displayName}’의 진행 단계를 확인하고 있어요.` : `${displayName}님의 진행 단계를 확인하고 있어요.`}
          size={'medium'}
          description={'카카오 알림톡으로 진행 과정을 안내드릴게요.'}
          graphic={<Svg src="icg_speech-balloon_md.svg" width={80} height={80} />}
        />
      </div>
      <Divider size={'large'} />
      <div className={'px-4.5 py-tiny-large'}>
        <div className={'pb-normal-large'}>
          <div className={'body-large-regular text-neutral-foreground-low pt-normal-large'}>{amountLabel}</div>
          <div className={'text-neutral-foreground-main heading-medium-bold'}>{data.refundAmount}</div>
          <Divider className={'my-normal-medium'} />
          <ResultTableRow label={'환급 신청일'} value={data.applyDate} />
          <ResultTableRow label={'예상 환급일'} value={'신청일부터 2~3개월'} />
          {data.applicationInfo && (
            <ResultTableRow label={'환급 계좌'} value={formatAccountBankNo(data.applicationInfo?.bank, data.applicationInfo?.accountNo)} />
          )}
          <Table
            headerCell={<TableCell variant={'header'}>이용료</TableCell>}
            contentCell={
              <TableCell className={'flex-1 text-right'}>
                <div>
                  <p className={'mb-1'}>결정 환급액의 {data.defaultFeeRate}%</p>
                  <p className={'text-13 text-gray-500'}>(부가세 별도)</p>
                </div>
              </TableCell>
            }
          />
          {data.discountRate !== '0' && (
            <Table
              headerCell={<TableCell variant={'header'}>이용료 할인</TableCell>}
              contentCell={
                <TableCell className={'flex-1 text-right'}>
                  <div>
                    <p className={'mb-1'}>제휴 할인 {data.discountRate}% 적용</p>
                    <p className={'text-13 text-gray-500'}>
                      결정 환급액의 <span className={'line-through'}>{data.defaultFeeRate}%</span>-&gt;{data.feeRate}% (vat 별도)
                    </p>
                  </div>
                </TableCell>
              }
            />
          )}
          {!isCorps && hasRegisteredCard && (
            <Table
              headerCell={<TableCell variant={'header'}>결제 카드</TableCell>}
              contentCell={
                <TableCell className={'flex-1'}>
                  <div className={'flex items-center justify-end gap-tiny-medium text-[15px]'}>
                    <div className={'flex items-center gap-1.5'}>
                      <Svg src={cardLogo} width={24} height={24} />
                      {cardName}({cardNumber.slice(-4)})
                    </div>
                    <BoxButton size={'small'} variant={'tonal-secondary'} onClick={handleCardClick}>
                      변경
                    </BoxButton>
                  </div>
                </TableCell>
              }
            />
          )}
        </div>
        <ApplyResultInfoView data={infoContents} />
      </div>
    </div>
  );
};
